import { Grid } from "@mui/material";
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { colors } from "Infrastructure/Themes/Colors";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { useReportingActorsQuery } from "Reporting/Api/Queries/useReportingActorsQuery";
import { ChartCard } from "./ChartCard";
import { ChartEmptyState } from "./ChartEmptyState";
import { PeriodSelector, type ReportingPeriod } from "./PeriodSelector";

const c = colors();

const TOOLTIP_STYLE = {
  contentStyle: {
    backgroundColor: c.BackgroundTile,
    border: `1px solid ${c.Border}`,
    borderRadius: 8,
  },
  labelStyle: { color: c.TextSecondary },
  itemStyle: { color: c.White },
  cursor: { fill: "rgba(255, 255, 255, 0.05)" },
} as const;

const AXIS_TICK = { fill: c.TextSecondary, fontSize: 12 } as const;

export const TopActorsChart = (): React.ReactNode => {
  const { t } = useResource();
  const [jobsPeriod, setJobsPeriod] = useState<ReportingPeriod>(null);
  const [earningsPeriod, setEarningsPeriod] = useState<ReportingPeriod>(null);
  const { data: jobsData, isLoading: jobsLoading } =
    useReportingActorsQuery(jobsPeriod);
  const { data: earningsData, isLoading: earningsLoading } =
    useReportingActorsQuery(earningsPeriod);

  const byJobs = jobsData?.topActorsByJobs ?? [];
  const byEarnings = earningsData?.topActorsByEarnings ?? [];

  const chartHeight = Math.max(
    200,
    Math.max(byJobs.length, byEarnings.length) * 40 + 40,
  );

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, lg: 6 }}>
        <ChartCard
          title={t(Resources.Reporting.TopActorsByJobs)}
          isLoading={jobsLoading}
          height={chartHeight}
          action={
            <PeriodSelector value={jobsPeriod} onChange={setJobsPeriod} />
          }
        >
          {byJobs.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart data={byJobs} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid stroke={c.Border} strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={AXIS_TICK}
                  axisLine={{ stroke: c.Border }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="actorName"
                  tick={AXIS_TICK}
                  axisLine={{ stroke: c.Border }}
                  tickLine={false}
                  width={120}
                />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(value) => [
                    Number(value),
                    t(Resources.Reporting.Jobs),
                  ]}
                />
                <Bar
                  dataKey="jobCount"
                  fill={c.PrimaryMain}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </Grid>

      <Grid size={{ xs: 12, lg: 6 }}>
        <ChartCard
          title={t(Resources.Reporting.TopActorsByEarnings)}
          isLoading={earningsLoading}
          height={chartHeight}
          action={
            <PeriodSelector
              value={earningsPeriod}
              onChange={setEarningsPeriod}
            />
          }
        >
          {byEarnings.length === 0 ? (
            <ChartEmptyState />
          ) : (
            <ResponsiveContainer width="100%" height={chartHeight}>
              <BarChart
                data={byEarnings}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid stroke={c.Border} strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  tick={AXIS_TICK}
                  axisLine={{ stroke: c.Border }}
                  tickLine={false}
                  tickFormatter={(v: number) =>
                    formatCurrency(v, CurrencyCode.EUR, 0)
                  }
                />
                <YAxis
                  type="category"
                  dataKey="actorName"
                  tick={AXIS_TICK}
                  axisLine={{ stroke: c.Border }}
                  tickLine={false}
                  width={120}
                />
                <Tooltip
                  {...TOOLTIP_STYLE}
                  formatter={(value) => [
                    formatCurrency(Number(value), CurrencyCode.EUR, 0),
                    t(Resources.Reporting.Earnings),
                  ]}
                />
                <Bar
                  dataKey="totalEarnings"
                  fill={c.InfoMain}
                  radius={[0, 4, 4, 0]}
                  maxBarSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </Grid>
    </Grid>
  );
};
